import { useCallback, useEffect, useState } from 'react';
import firebase from 'firebase/app';
import { firestoreCollection } from 'util/constants';
import { serverQuery } from 'util/serverRead';

// Read-only figures for the Admin Dashboard. Everything here is a one-off read
// from the database (serverQuery), with no writes. Sizes are stored in bytes,
// sometimes as strings, so they are coerced with Number().

export type CategoryStat = { files: number; bytes: number };

export type ProjectStorage = { id: string; title: string; client: string; bytes: number; files: number };

export type AdminUser = { id: string; email: string; isAdmin: boolean };

/** A project with no files of any kind (videos, assets or gallery images). */
export type EmptyProject = { id: string; title: string; client: string; date: number; records: number; notes: number };

/** Files whose project no longer exists (or whose record is gone). `inBin`: already in Admin › Deleted items. */
export type OrphanStat = { files: number; bytes: number; projects: number; inBin: number };

export type AdminStats = {
  projects: { total: number; complete: number; incomplete: number; noClient: number };
  records: { total: number; withoutFiles: number };
  videos: { total: number; bytes: number; byCategory: Record<string, CategoryStat> };
  assets: CategoryStat;
  gallery: CategoryStat;
  images: number;
  notes: number;
  totalBytes: number;
  topProjects: ProjectStorage[];
  /** Data-quality views (Phase 2, 5 Nov 2026) */
  smallestProjects: ProjectStorage[];
  emptyProjects: EmptyProject[];
  orphans: OrphanStat;
  users: AdminUser[];
  loadedAt: Date;
};

const toBytes = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

// From the database, never the browser's copy: that showed zeros on a slow connection (5 Nov 2026).
const getAll = async (collection: string) => (await serverQuery(firebase.firestore().collection(collection))).docs;

const useAdminStats = () => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [projects, records, videos, assets, gallery, images, notes, users] = await Promise.all([
        getAll(firestoreCollection.projects),
        getAll(firestoreCollection.records),
        getAll(firestoreCollection.videos),
        getAll(firestoreCollection.assets),
        getAll(firestoreCollection.gallery),
        getAll('images'),
        getAll(firestoreCollection.notes),
        getAll(firestoreCollection.users),
      ]);

      // Projects
      const projectInfo: Record<string, { title: string; client: string; date: number }> = {};
      let complete = 0;
      let noClient = 0;
      projects.forEach((doc) => {
        const p = doc.data();
        if (p.complete === true) complete++;
        if (!p.client || !String(p.client).trim()) noClient++;
        projectInfo[doc.id] = { title: p.title || '(untitled)', client: p.client || '', date: Number(p.date) || 0 };
      });

      // Records → project lookup, so video sizes can be rolled up per project
      const recordProject: Record<string, string> = {};
      const recordsPerProject: Record<string, number> = {};
      records.forEach((doc) => {
        const pid = doc.data().projectID;
        recordProject[doc.id] = pid;
        if (pid) recordsPerProject[pid] = (recordsPerProject[pid] || 0) + 1;
      });
      const notesPerProject: Record<string, number> = {};
      notes.forEach((doc) => {
        const pid = doc.data().projectID;
        if (pid) notesPerProject[pid] = (notesPerProject[pid] || 0) + 1;
      });
      // Files with no project to belong to: reported here; Admin › Deleted items deals with them.
      const orphanProjects = new Set<string>();
      const orphans: OrphanStat = { files: 0, bytes: 0, projects: 0, inBin: 0 };
      const orphan = (projectId: string | undefined, bytes: number, binned: unknown) => {
        orphans.files++;
        orphans.bytes += bytes;
        if (binned) orphans.inBin++;
        orphanProjects.add(projectId || '(none)');
      };

      // Videos
      const byCategory: Record<string, CategoryStat> = {};
      const perProject: Record<string, { bytes: number; files: number }> = {};
      const recordsWithFiles = new Set<string>();
      let videoBytes = 0;
      videos.forEach((doc) => {
        const v = doc.data();
        const bytes = toBytes(v.size);
        const category = v.category || 'uncategorised';
        videoBytes += bytes;
        byCategory[category] = byCategory[category] || { files: 0, bytes: 0 };
        byCategory[category].files++;
        byCategory[category].bytes += bytes;
        if (v.recordID) recordsWithFiles.add(v.recordID);
        const projectId = recordProject[v.recordID];
        if (projectId) {
          perProject[projectId] = perProject[projectId] || { bytes: 0, files: 0 };
          perProject[projectId].bytes += bytes;
          perProject[projectId].files++;
        }
        if (!projectId || !projectInfo[projectId]) orphan(projectId, bytes, v.bin);
      });

      const sumFiles = (docs: firebase.firestore.QueryDocumentSnapshot[]): CategoryStat =>
        docs.reduce(
          (acc, doc) => {
            const d = doc.data();
            const bytes = toBytes(d.size);
            acc.files++;
            acc.bytes += bytes;
            if (d.projectID) {
              perProject[d.projectID] = perProject[d.projectID] || { bytes: 0, files: 0 };
              perProject[d.projectID].bytes += bytes;
              perProject[d.projectID].files++;
            }
            if (!d.projectID || !projectInfo[d.projectID]) orphan(d.projectID, bytes, d.bin);
            return acc;
          },
          { files: 0, bytes: 0 } as CategoryStat,
        );
      const assetStat = sumFiles(assets);
      const galleryStat = sumFiles(gallery);

      const topProjects: ProjectStorage[] = Object.keys(perProject)
        .map((id) => ({
          id,
          title: projectInfo[id]?.title || '(deleted project)',
          client: projectInfo[id]?.client || '',
          ...perProject[id],
        }))
        .sort((a, b) => b.bytes - a.bytes)
        .slice(0, 10);

      // Smallest projects that still exist and hold at least one file.
      const smallestProjects: ProjectStorage[] = Object.keys(perProject)
        .filter((id) => projectInfo[id])
        .map((id) => ({ id, title: projectInfo[id].title, client: projectInfo[id].client, ...perProject[id] }))
        .sort((a, b) => a.bytes - b.bytes || a.files - b.files)
        .slice(0, 10);

      // Projects with no files at all, newest first.
      const emptyProjects: EmptyProject[] = Object.keys(projectInfo)
        .filter((id) => !perProject[id])
        .map((id) => ({ id, ...projectInfo[id], records: recordsPerProject[id] || 0, notes: notesPerProject[id] || 0 }))
        .sort((a, b) => b.date - a.date);
      orphans.projects = orphanProjects.size;

      setStats({
        projects: { total: projects.length, complete, incomplete: projects.length - complete, noClient },
        records: { total: records.length, withoutFiles: records.filter((r) => !recordsWithFiles.has(r.id)).length },
        videos: { total: videos.length, bytes: videoBytes, byCategory },
        assets: assetStat,
        gallery: galleryStat,
        images: images.length,
        notes: notes.length,
        totalBytes: videoBytes + assetStat.bytes + galleryStat.bytes,
        topProjects,
        smallestProjects,
        emptyProjects,
        orphans,
        users: users
          .map((doc) => ({ id: doc.id, email: doc.data().email || '(no email)', isAdmin: doc.data().isAdmin === true }))
          .sort((a, b) => a.email.localeCompare(b.email)),
        loadedAt: new Date(),
      });
    } catch (e) {
      console.error('Admin stats failed to load:', e);
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { stats, loading, error, reload: load };
};

export const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value >= 100 || i === 0 ? 0 : value >= 10 ? 1 : 2)} ${units[i]}`;
};

export default useAdminStats;
